<template>
  <div class="cat-page">

    <!-- Hero -->
    <section class="cat-hero">
      <div class="max-w-screen-xl mx-auto px-6">
        <span class="cat-eyebrow">Assortiment</span>
        <h1 class="cat-hero-title">Alle categorieën</h1>
        <p class="cat-hero-sub">Blader door ons volledige aanbod, van zonnepanelen tot laadpalen en thuisbatterijen, en vind snel wat je zoekt.</p>
      </div>
    </section>

    <div class="max-w-screen-xl mx-auto px-6">

      <!-- Keuzehulp-instap -->
      <router-link to="/keuzehulp" class="cat-kh">
        <span class="cat-kh-icon"><i class="bi bi-compass"></i></span>
        <span class="cat-kh-text">
          <strong>Moeite met kiezen?</strong>
          <span>Doe de keuzehulp en vind in 1 minuut het juiste product.</span>
        </span>
        <span class="cat-kh-arrow">Start <i class="bi bi-arrow-right"></i></span>
      </router-link>

      <!-- Loading -->
      <div v-if="loading" class="cat-loading">
        <div class="cat-spinner"></div>
        <p>Categorieën laden…</p>
      </div>

      <template v-else>
        <!-- Hoofdcategorieën als kaarten -->
        <section class="cat-block">
          <h2 class="cat-block-title">Hoofdcategorieën</h2>
          <div class="cat-grid">
            <router-link
              v-for="category in mainCategories"
              :key="category.id"
              :to="`/categorie/${category.slug}`"
              class="cat-card"
            >
              <span class="cat-card-media">
                <img v-if="category.image" :src="sized(category.image.src)" :alt="category.name" loading="lazy">
                <span v-else class="cat-card-icon"><i :class="getCategoryIcon(category.slug)"></i></span>
              </span>
              <span class="cat-card-body">
                <span class="cat-card-name">{{ category.name }}</span>
                <span v-if="category.description" class="cat-card-desc">{{ stripHtml(category.description) }}</span>
                <span class="cat-card-meta">
                  <span class="cat-card-count"><i class="bi bi-box-seam"></i> {{ category.count }} {{ category.count === 1 ? 'product' : 'producten' }}</span>
                  <span class="cat-card-arrow"><i class="bi bi-arrow-right"></i></span>
                </span>
              </span>
            </router-link>
          </div>
        </section>

        <!-- Alle categorieën, compacte lijst -->
        <section v-if="allCategories.length" class="cat-block">
          <h2 class="cat-block-title">Alle categorieën</h2>
          <div class="cat-list">
            <router-link
              v-for="category in allCategories"
              :key="category.id"
              :to="`/categorie/${category.slug}`"
              class="cat-list-item"
            >
              <span class="cat-list-icon"><i :class="getCategoryIcon(category.slug)"></i></span>
              <span class="cat-list-body">
                <span class="cat-list-name">{{ category.name }}</span>
                <span class="cat-list-count">{{ category.count }} producten</span>
              </span>
              <i class="bi bi-chevron-right cat-list-arrow"></i>
            </router-link>
          </div>
        </section>
      </template>
    </div>

    <!-- CTA -->
    <section class="cat-cta">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="cat-cta-inner">
          <div>
            <h2 class="cat-cta-title">Hulp nodig bij je keuze?</h2>
            <p class="cat-cta-sub">Onze energiespecialisten helpen je gratis de juiste oplossing te vinden voor jouw situatie.</p>
          </div>
          <div class="cat-cta-actions">
            <a href="mailto:info@plug2charge.nl" class="cat-btn cat-btn--primary"><i class="bi bi-envelope"></i> Stuur een mail</a>
            <a href="/klantenservice#livechat" class="cat-btn cat-btn--ghost"><i class="bi bi-chat-dots"></i> Live chat</a>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useProductStore } from '@stores/products'

const productStore = useProductStore()

const loading    = computed(() => productStore.loading)
const categories = computed(() => productStore.categories || [])

const mainCategories = computed(() =>
  [...categories.value]
    .filter(cat => cat.parent === 0 && cat.slug !== 'uncategorized' && cat.count > 0)
    .sort((a, b) => b.count - a.count)
)

const allCategories = computed(() =>
  [...categories.value]
    .filter(cat => cat.slug !== 'uncategorized' && cat.count > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
)

// 400px-variant via de media-proxy
const sized = (url) => {
  if (!url) return url
  const i = url.indexOf('/wp-content/')
  return i === -1 ? url : `${url.slice(i)}?w=400`
}

const getCategoryIcon = (slug) => ({
  'zonnepanelen':       'bi bi-sun',
  'complete-sets':      'bi bi-box-seam',
  'balkon-zonnepanelen':'bi bi-sun',
  'laadpalen':          'bi bi-ev-station',
  'thuislaadpalen':     'bi bi-ev-station',
  'slimme-laadpalen':   'bi bi-lightning-charge',
  '3-fase-laadpalen':   'bi bi-lightning-charge',
  'laadkabels':         'bi bi-plug',
  'wallboxen':          'bi bi-lightning-charge',
  'micro-omvormers':    'bi bi-gear',
  'omvormers':          'bi bi-gear',
  'thuisbatterijen':    'bi bi-battery-charging',
  'all-in-one-batterijen':'bi bi-battery-full',
  'stekkerbatterij':    'bi bi-battery',
  'draagbare-batterijen':'bi bi-battery',
  'accu':               'bi bi-battery',
  'montage':            'bi bi-hammer',
  'montage-materiaal':  'bi bi-hammer',
  'kabels':             'bi bi-plug',
  'accessoires':        'bi bi-gear',
}[slug] || 'bi bi-folder')

const stripHtml = (html) => {
  if (!html) return ''
  const text = html.replace(/<[^>]*>/g, '').trim()
  return text.length > 90 ? text.substring(0, 90) + '…' : text
}

onMounted(() => {
  productStore.fetchCategories()
  if (typeof document !== 'undefined') document.title = 'Alle categorieën | Plug2Charge.nl'
})
</script>

<style scoped>
.cat-page { background: #fafafa; min-height: 100vh; }

/* Hero — vlak donker, in lijn met de rest */
.cat-hero {
  background: #111;
  color: #fff;
  padding: 3.5rem 0;
}
.cat-eyebrow {
  display: inline-block;
  background: rgba(255, 213, 0, 0.15);
  color: #FFD500;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.3rem 1rem;
  border-radius: 50px;
  border: 1px solid rgba(255, 213, 0, 0.3);
  margin-bottom: 1rem;
}
.cat-hero-title {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: 0 0 0.75rem;
}
.cat-hero-sub {
  font-size: 1.0625rem;
  color: #bbb;
  line-height: 1.6;
  max-width: 640px;
  margin: 0;
}

/* Keuzehulp-instap (zelfde patroon als op de categoriepagina) */
.cat-kh {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin: 2rem 0 0;
  padding: 0.85rem 1.1rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  text-decoration: none;
  transition: border-color .18s ease, box-shadow .18s ease;
}
.cat-kh:hover { border-color: #FFD500; box-shadow: 0 4px 16px rgba(0,0,0,.06); }
.cat-kh-icon { flex-shrink: 0; width: 40px; height: 40px; border-radius: 50%; background: #FFF6CC; display: flex; align-items: center; justify-content: center; color: #b8860b; font-size: 1.1rem; }
.cat-kh-text { flex: 1; min-width: 0; line-height: 1.3; }
.cat-kh-text strong { display: block; font-size: 0.9rem; color: #111; }
.cat-kh-text span { display: block; font-size: 0.8rem; color: #777; }
.cat-kh-arrow { flex-shrink: 0; color: #b8860b; font-weight: 600; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 0.25rem; }

/* Loading */
.cat-loading { display: flex; flex-direction: column; align-items: center; padding: 4rem 2rem; color: #888; }
.cat-spinner { width: 3rem; height: 3rem; border: 4px solid #eee; border-top-color: #FFD500; border-radius: 50%; animation: cat-spin 1s linear infinite; margin-bottom: 1rem; }
@keyframes cat-spin { to { transform: rotate(360deg); } }

/* Blokken */
.cat-block { margin: 3rem 0 0; }
.cat-block:first-of-type { margin-top: 2.5rem; }
.cat-block-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 1.25rem;
}

/* Kaartgrid */
.cat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
}
.cat-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
}
.cat-card:hover { border-color: #FFD500; box-shadow: 0 8px 28px rgba(0,0,0,.08); transform: translateY(-3px); }
.cat-card-media {
  display: block;
  height: 170px;
  background: #fff;
  overflow: hidden;
  position: relative;
}
.cat-card-media img { width: 100%; height: 100%; object-fit: cover; transition: transform .3s ease; }
.cat-card:hover .cat-card-media img { transform: scale(1.04); }
.cat-card-icon { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 3rem; color: #d8d8d8; }
.cat-card-body { padding: 1.25rem; flex: 1; display: flex; flex-direction: column; }
.cat-card-name { font-size: 1.15rem; font-weight: 800; color: #111; margin-bottom: 0.4rem; }
.cat-card-desc { font-size: 0.875rem; color: #666; line-height: 1.55; flex: 1; }
.cat-card-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 1rem; padding-top: 0.9rem; border-top: 1px solid #f0f0f0; }
.cat-card-count { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; color: #666; }
.cat-card-count i { color: #b8860b; }
.cat-card-arrow { color: #b8860b; transition: transform .2s ease; }
.cat-card:hover .cat-card-arrow { transform: translateX(4px); }

/* Compacte lijst */
.cat-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
}
.cat-list-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  text-decoration: none;
  transition: border-color .18s ease, box-shadow .18s ease;
}
.cat-list-item:hover { border-color: #FFD500; box-shadow: 0 4px 14px rgba(0,0,0,.05); }
.cat-list-icon { flex-shrink: 0; width: 42px; height: 42px; border-radius: 10px; background: #f6f7f8; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; color: #b8860b; }
.cat-list-body { flex: 1; min-width: 0; line-height: 1.3; }
.cat-list-name { display: block; font-size: 0.95rem; font-weight: 700; color: #111; }
.cat-list-count { display: block; font-size: 0.8rem; color: #888; }
.cat-list-arrow { color: #ccc; transition: color .18s, transform .18s; }
.cat-list-item:hover .cat-list-arrow { color: #b8860b; transform: translateX(3px); }

/* CTA */
.cat-cta { padding: 3.5rem 0; margin-top: 3rem; }
.cat-cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 20px;
  padding: 2.25rem 2.5rem;
}
.cat-cta-title { font-size: 1.5rem; font-weight: 900; color: #111; margin: 0 0 0.4rem; }
.cat-cta-sub { font-size: 1rem; color: #666; margin: 0; max-width: 480px; }
.cat-cta-actions { display: flex; gap: 0.75rem; flex-shrink: 0; }
.cat-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  white-space: nowrap;
  transition: background .15s ease, border-color .15s ease, transform .15s ease;
}
.cat-btn--primary { background: #FFD500; color: #111; }
.cat-btn--primary:hover { background: #ffb800; transform: translateY(-1px); }
.cat-btn--ghost { background: #fff; color: #333; border: 1px solid #e5e7eb; }
.cat-btn--ghost:hover { border-color: #FFD500; color: #111; }

@media (max-width: 860px) {
  .cat-cta-inner { flex-direction: column; align-items: flex-start; }
  .cat-cta-actions { width: 100%; }
  .cat-btn { flex: 1; justify-content: center; }
}
@media (max-width: 640px) {
  .cat-grid { grid-template-columns: 1fr; }
  .cat-list { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .cat-card, .cat-card-arrow, .cat-card-media img, .cat-list-arrow, .cat-btn { transition: none; }
}
</style>
